"use client"

import { useCallback } from "react"
import consola from "consola"
import { use$ } from "@legendapp/state/react"
import { getChromeExtensionRuntime, sendChromeExtensionWebphoneMessage } from "@/lib/chromeExtensionMessaging"
import { WebphoneMessageSchema, type TWebphoneMessage } from "@/schemas/webphone.schema"
import { syncStore$ } from "@/stores/sync.v2.store"

interface UseWebphoneMessageProps {
  /**
   * Optional callback to be called when a message is sent successfully
   */
  onMessageSent?: () => void
  /**
   * Optional callback to be called when there's an error sending the message
   */
  onError?: (error: Error) => void
}

const webphoneMessageLogger = consola.withTag("webphone-message")

/**
 * Focus the detached webphone window if it exists
 *
 * @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns the webphone sidebar.
 */
function focusDetachedWebphone() {
  // if (typeof window !== "undefined" && (window as unknown as { focusDetachedWebphone?: () => void }).focusDetachedWebphone) {
  //   ;(window as unknown as { focusDetachedWebphone: () => void }).focusDetachedWebphone()
  // }
}

/**
 * Send a message to the detached webphone iframe if it exists.
 * Returns true if the message was sent, false otherwise.
 *
 * @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns the webphone sidebar.
 */
function sendMessageToDetachedWebphone(_message: unknown): boolean {
  // if (typeof window !== "undefined" && (window as unknown as { sendMessageToDetachedWebphone?: (message: unknown) => boolean }).sendMessageToDetachedWebphone) {
  //   return (window as unknown as { sendMessageToDetachedWebphone: (message: unknown) => boolean }).sendMessageToDetachedWebphone(message)
  // }
  return false
}

function postMessageToParentWindow(message: TWebphoneMessage) {
  if (typeof window === "undefined") return

  const targetWindow = window.top ?? window
  targetWindow.postMessage(message, "*")
}

/**
 * Hook for sending messages to the Chrome extension webphone runtime.
 *
 * @param props Configuration options for the hook
 * @returns Object containing the sendMessage function
 */
export function useWebphoneMessage(props?: UseWebphoneMessageProps) {
  const { onMessageSent, onError } = props ?? {}
  const chromeExtensionId = use$(() => syncStore$.cluster.chromeExtensionWebstoreId.get())

  const sendMessage = useCallback(
    async (message: TWebphoneMessage) => {
      try {
        const validatedMessage = WebphoneMessageSchema.parse(message)
        const runtime = getChromeExtensionRuntime()

        if (chromeExtensionId && runtime) {
          webphoneMessageLogger.info("Sending webphone command through Chrome extension runtime", {
            extensionId: chromeExtensionId,
            action: validatedMessage.action,
          })

          await sendChromeExtensionWebphoneMessage(chromeExtensionId, validatedMessage, runtime)
          onMessageSent?.()
          return true
        }

        webphoneMessageLogger.warn("Chrome extension runtime unavailable for webphone command, falling back to parent window delivery", {
          extensionId: chromeExtensionId,
          hasRuntime: Boolean(runtime),
          action: validatedMessage.action,
        })

        focusDetachedWebphone()
        const sentToDetached = sendMessageToDetachedWebphone(validatedMessage)
        if (!sentToDetached) {
          postMessageToParentWindow(validatedMessage)
        }

        onMessageSent?.()
        return true
      } catch (error) {
        const webphoneError = error instanceof Error ? error : new Error("Failed to send message to webphone")
        onError?.(webphoneError)
        return false
      }
    },
    [chromeExtensionId, onError, onMessageSent]
  )

  return {
    sendMessage,
  }
}
